import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RichTextEditor } from "@/components/ui/rich-text-editor";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Database } from "@/integrations/supabase/types";
import { useCreatePolicyVersion, useUpdatePolicyVersion, usePublishPolicyVersion, usePolicyVersions } from "@/hooks/superadmin/usePolicyVersions";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { isMarkdown, markdownToHtml } from "@/utils/markdownToHtml";

type PolicyVersion = Database["public"]["Tables"]["policy_versions"]["Row"];

interface PolicyVersionEditorProps {
  isOpen: boolean;
  onClose: () => void;
  version: PolicyVersion | null;
  policyType: "privacy_policy" | "terms_acceptance" | "cookies" | "marketing";
  onSave?: () => void;
}

const POLICY_TYPE_LABELS: Record<string, string> = {
  privacy_policy: "Privacy Policy",
  terms_acceptance: "Terms of Service",
  cookies: "Cookie Policy",
  marketing: "Marketing Consent",
};

const POLICY_TYPE_PREFIXES: Record<string, string> = {
  privacy_policy: "privacy",
  terms_acceptance: "terms",
  cookies: "cookies",
  marketing: "marketing",
};

/**
 * Generate the next version number for a policy type
 * Format: {prefix}_v{version}_{year}_{month}
 */
async function generateNextVersion(policyType: "privacy_policy" | "terms_acceptance" | "cookies" | "marketing"): Promise<string> {
  const prefix = POLICY_TYPE_PREFIXES[policyType];
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  
  // Fetch all existing versions for this policy type
  const { data: existingVersions, error } = await supabase
    .from("policy_versions")
    .select("version")
    .eq("policy_type", policyType);

  if (error) {
    console.error("Error fetching existing versions:", error);
    // Fallback to v1 if we can't fetch
    return `${prefix}_v1_${year}_${month}`;
  }

  // Extract version numbers from existing versions
  const versionPattern = new RegExp(`${prefix}_v(\\d+)_\\d{4}_\\d{2}`);
  const versionNumbers: number[] = [];

  existingVersions?.forEach((v) => {
    const match = v.version.match(versionPattern);
    if (match) {
      versionNumbers.push(parseInt(match[1], 10));
    }
  });

  // Find the highest version number and increment
  const nextVersion = versionNumbers.length > 0 ? Math.max(...versionNumbers) + 1 : 1;

  return `${prefix}_v${nextVersion}_${year}_${month}`;
}

export const PolicyVersionEditor: React.FC<PolicyVersionEditorProps> = ({
  isOpen,
  onClose,
  version,
  policyType,
  onSave,
}) => {
  const [formData, setFormData] = useState({
    version: "",
    content: "",
    effective_date: "",
  });
  const [isGeneratingVersion, setIsGeneratingVersion] = useState(false);
  const { toast } = useToast();
  const createMutation = useCreatePolicyVersion();
  const updateMutation = useUpdatePolicyVersion();
  const publishMutation = usePublishPolicyVersion();

  useEffect(() => {
    if (version) {
      // Editing existing version - use existing version string
      // Convert Markdown to HTML if needed for backward compatibility
      let content = version.content;
      if (isMarkdown(content)) {
        content = markdownToHtml(content);
      }
      
      setFormData({
        version: version.version,
        content: content,
        effective_date: version.effective_date
          ? new Date(version.effective_date).toISOString().split("T")[0]
          : "",
      });
    } else if (isOpen) {
      // Creating new version - auto-generate version number
      setIsGeneratingVersion(true);
      generateNextVersion(policyType)
        .then((nextVersion) => {
          setFormData({
            version: nextVersion,
            content: "",
            effective_date: "",
          });
        })
        .catch((error) => {
          console.error("Error generating version:", error);
          toast({
            title: "Error",
            description: "Failed to generate version number",
            variant: "destructive",
          });
        })
        .finally(() => {
          setIsGeneratingVersion(false);
        });
    }
  }, [version, isOpen, policyType, toast]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (version) {
        // Update existing version
        await updateMutation.mutateAsync({
          id: version.id,
          data: {
            version: formData.version,
            content: formData.content,
            effective_date: formData.effective_date || null,
          },
        });
        toast({
          title: "Success",
          description: "Policy version updated successfully",
        });
        onSave?.();
      } else {
        // Create new version
        await createMutation.mutateAsync({
          policy_type: policyType,
          version: formData.version,
          content: formData.content,
          effective_date: formData.effective_date || null,
          status: "draft",
        });
        toast({
          title: "Success",
          description: "Policy version created successfully",
        });
      }
      onSave?.();
      onClose();
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to save policy version",
        variant: "destructive",
      });
    }
  };

  const handlePublish = async () => {
    if (!version) return;

    try {
      await publishMutation.mutateAsync(version.id);
      toast({
        title: "Success",
        description: "Policy version published successfully",
      });
      onClose();
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to publish policy version",
        variant: "destructive",
      });
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending || publishMutation.isPending;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {version ? "Edit" : "Create"} {POLICY_TYPE_LABELS[policyType]} Version
          </DialogTitle>
          <DialogDescription>
            {version ? "Update the policy version details" : "Create a new policy version"}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="version">Version</Label>
              <Input
                id="version"
                value={formData.version}
                onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                placeholder={isGeneratingVersion ? "Generating version..." : "e.g., privacy_v1_2025_12"}
                required
                disabled={isLoading || isGeneratingVersion || !version} // Disable when creating (auto-generated)
                readOnly={!version} // Read-only when creating new version
                className={!version ? "bg-muted cursor-not-allowed" : ""}
              />
              <p className="text-xs text-muted-foreground">
                {version 
                  ? "Version can be edited for existing versions"
                  : "Version is automatically generated based on existing versions"
                }
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="effective_date">Effective Date (Optional)</Label>
              <Input
                id="effective_date"
                type="date"
                value={formData.effective_date}
                onChange={(e) => setFormData({ ...formData, effective_date: e.target.value })}
                disabled={isLoading}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="content">Content</Label>
              <RichTextEditor
                value={formData.content}
                onChange={(html) => setFormData({ ...formData, content: html })}
                placeholder="Enter your policy document content here. Use the toolbar to format text with headings, bold, italic, lists, and links."
                disabled={isLoading}
              />
              <p className="text-xs text-muted-foreground">
                Use the formatting toolbar to style your content. Existing Markdown content will be automatically converted to Rich Text format.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={isLoading}>
              Cancel
            </Button>
            {version && version.status !== "active" && (
              <Button
                type="button"
                variant="default"
                onClick={handlePublish}
                disabled={isLoading}
              >
                {publishMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Publishing...
                  </>
                ) : (
                  "Publish"
                )}
              </Button>
            )}
            <Button type="submit" disabled={isLoading || isGeneratingVersion}>
              {isLoading || isGeneratingVersion ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {isGeneratingVersion ? "Generating..." : "Saving..."}
                </>
              ) : version ? (
                "Update"
              ) : (
                "Create"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

